import { useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { useSearchParams } from 'libs/router'
import { SelectClickTypes } from '@table-library/react-table-library/select'
import {
	ActionsSelect,
	CardHeader,
	CardWrapper,
	CtaButton,
	DeleteConfirmModal,
	EmptyTable,
	Header,
	NameDialog,
	ProductSettingsTabs,
	SearchInput
} from 'shared/components'
import { useDevice, useModal, useSearch, useTableSelect } from 'shared/hooks'
import type { ConditionItem } from 'features/conditions/types/conditions.types'
import { useActionFilters } from './hooks/useActionFilters'
import { useContextMenuOptions } from './hooks/useContextMenuOptions'
import { useFetchConditions } from './queries/useFetchConditions'
import { useCreateConditionMutation } from './mutations/useCreateConditionMutation'
import { useEditConditionMutation } from './mutations/useEditConditionMutation'
import { useDeleteConditionsMutation } from './mutations/useDeleteConditionsMutation'
import { useDeleteConditionMutation } from './mutations/useDeleteConditionMutation'
import { TableSection } from './organisms/TableSection'

const ConditionsPage = () => {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const { isMobileOrTablet } = useDevice()
	const { handleSearch } = useSearch({ name: 'search' })
	const [checkedConditions, setCheckedConditions] = useState<number[]>([])
	const [idToBeRemoved, setIdToBeRemoved] = useState<string | null>(null)
	const [editedCondition, setEditedCondition] = useState<ConditionItem | null>(null)
	const { conditions, isConditionsLoading } = useFetchConditions()
	const { select } = useTableSelect({ nodes: conditions?.data?.results ?? [] }, onSelectChange, {
		clickType: SelectClickTypes.ButtonClick
	})
	const [actionsFilter] = useActionFilters(!checkedConditions.length)

	const {
		isOpen: isAddDialogOpen,
		openModal: openAddDialog,
		closeModal: closeAddDialog
	} = useModal()
	const {
		isOpen: isDeleteModalOpen,
		openModal: openDeleteModal,
		closeModal: closeDeleteModal
	} = useModal()
	const {
		isOpen: isBulkDeleteModalOpen,
		openModal: openBulkDeleteModal,
		closeModal: closeBulkDeleteModal
	} = useModal()

	const { createCondition, createConditionLoading } = useCreateConditionMutation({
		onSuccess: closeAddDialog
	})

	const { editConditionMutation, isEditConditionLoading } = useEditConditionMutation({
		onSuccess: () => setEditedCondition(null)
	})

	const { deleteConditionMutation } = useDeleteConditionMutation({
		id: idToBeRemoved,
		onSettled: closeDeleteModal
	})

	const { deleteConditionsMutation } = useDeleteConditionsMutation({
		ids: checkedConditions,
		onSettled: () => {
			select.fns.onRemoveAll()
			closeBulkDeleteModal()
		}
	})

	function onSelectChange(action: any, state: any) {
		setCheckedConditions(state.ids)
	}

	const handleDelete = (id: number) => {
		setIdToBeRemoved(id.toString())
		openDeleteModal()
	}

	const menuOptions = useContextMenuOptions({ onEdit: setEditedCondition, onDelete: handleDelete })

	const handleAction = (action: string) => {
		if (action === 'delete') openBulkDeleteModal()
	}

	const renderContent = () => {
		if (isConditionsLoading) return <EmptyTable isLoading />
		if (!conditions?.data?.results?.length)
			return (
				<EmptyTable text={searchParams.toString() ? t('No results') : t('No conditions yet')} />
			)

		return <TableSection conditions={conditions} select={select} menuOptions={menuOptions} />
	}

	return (
		<>
			<Header
				title={t('Products')}
				tabs={<ProductSettingsTabs />}
				actions={
					!isMobileOrTablet && (
						<ActionsSelect options={actionsFilter.options} onChange={handleAction} />
					)
				}
			/>
			<div className="px-5 lg:flex lg:max-h-[calc(100vh-113.5px-4rem)] lg:flex-col lg:px-0">
				<CardWrapper className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
					<CardHeader
						title={t('Conditions')}
						description={t('Condition categories describing how the product has been used.')}>
						<SearchInput
							variant="filled"
							className="sm:w-64"
							label={t('Search conditions')}
							placeholder={t('Search conditions')}
							onChange={handleSearch}
						/>
						<CtaButton text={t('Add condition')} onClick={openAddDialog} />
					</CardHeader>
					{renderContent()}
				</CardWrapper>
			</div>

			{isAddDialogOpen && (
				<NameDialog
					title={t('Add condition')}
					submitText={t('Create')}
					isLoading={createConditionLoading}
					closeModal={closeAddDialog}
					onSubmit={createCondition}
				/>
			)}
			{editedCondition && (
				<NameDialog
					title={t('Edit condition')}
					submitText={t('Save')}
					defaultValue={editedCondition.name}
					isLoading={isEditConditionLoading}
					closeModal={() => setEditedCondition(null)}
					onSubmit={(data) => editConditionMutation({ id: editedCondition.id, data })}
				/>
			)}
			{isDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete condition')}
					isOpened={isDeleteModalOpen}
					closeModal={closeDeleteModal}
					confirm={deleteConditionMutation}>
					<p>{t('You are about to remove a condition. Are you sure you want to proceed?')}</p>
				</DeleteConfirmModal>
			)}
			{isBulkDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete selected conditions')}
					isOpened={isBulkDeleteModalOpen}
					closeModal={closeBulkDeleteModal}
					confirm={deleteConditionsMutation}>
					<p>
						{t(
							'You are about to remove {{ count }} conditions. Are you sure you want to proceed?',
							{ count: checkedConditions.length }
						)}
					</p>
				</DeleteConfirmModal>
			)}
		</>
	)
}

export default ConditionsPage
